import {
  type Organization,
  type OrgRole,
  orgPermissionSet,
  type Permission,
  type Workspace,
  type WorkspaceRole,
  workspacePermissionSet,
} from '@noconflict/contracts';
import { createContext, useContext } from 'react';
import { z } from 'zod';

const STORAGE_KEY = 'ncc.lastOrg';
const stored = z.object({ slug: z.string().min(1) });

/** The org the person last worked in. A convenience for `/` only: access is decided by the URL and RLS. */
export function readLastOrgSlug(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = stored.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data.slug : null;
  } catch {
    return null;
  }
}

export function writeLastOrgSlug(slug: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ slug }));
  } catch {
    // Storage blocked: `/` falls back to the first membership.
  }
}

type OwnMembership = { organization_id: string; role: OrgRole };
type OwnGrant = { organization_id: string; workspace_id: string | null; role: WorkspaceRole };

export type OrgScopeData = {
  /** The active org: the one named in the URL, or (outside an org route) the last used, or the first. */
  org: Organization | null;
  /**
   * The org the shell shows (switcher, workspace navigation): `org`, or the fallback when the URL names an
   * org the caller cannot see, so a not-found page still has a way back. Role, workspaces and permissions
   * below belong to this org.
   */
  shellOrg: Organization | null;
  /** True when the URL names an org the caller cannot see. Never distinguishes "missing" from "forbidden". */
  notFound: boolean;
  organizations: Organization[];
  role: OrgRole | null;
  /** Visible workspaces of the active org. */
  activeWorkspaces: Workspace[];
  archivedWorkspaces: Workspace[];
  workspaceBySlug: (slug: string) => Workspace | null;
  /** Org-level permission. Decides what the UI shows; RLS and the RPCs decide what happens. */
  can: (permission: Permission) => boolean;
  canIn: (workspaceId: string, permission: Permission) => boolean;
};

export function buildOrgScope(input: {
  organizations: Organization[];
  memberships: OwnMembership[];
  grants: OwnGrant[];
  workspaces: Workspace[];
  slugInUrl: string | null;
  lastSlug: string | null;
}): OrgScopeData {
  const { slugInUrl, lastSlug } = input;
  // "My orgs" are the ones I hold a membership in, never "every org I can read". The two were the same until the
  // staff path: a staff member at aal2 can SELECT every organization, deleted ones included, and those
  // belong in /admin, not in their own switcher or as the landing place of `/`.
  const organizations = input.organizations.filter(
    (o) => o.deleted_at === null && input.memberships.some((m) => m.organization_id === o.id),
  );
  const bySlug = (slug: string | null) => organizations.find((o) => o.slug === slug) ?? null;
  const fallback = bySlug(lastSlug) ?? organizations[0] ?? null;
  const org = slugInUrl ? bySlug(slugInUrl) : fallback;
  const shellOrg = org ?? fallback;
  const role = input.memberships.find((m) => m.organization_id === shellOrg?.id)?.role ?? null;
  const grants = input.grants.filter((g) => g.organization_id === shellOrg?.id);
  const workspaces = input.workspaces.filter((w) => w.organization_id === shellOrg?.id);
  const orgSet = orgPermissionSet(role);
  return {
    org,
    shellOrg,
    notFound: slugInUrl !== null && org === null,
    organizations,
    role,
    activeWorkspaces: workspaces.filter((w) => w.archived_at === null),
    archivedWorkspaces: workspaces.filter((w) => w.archived_at !== null),
    workspaceBySlug: (slug) => workspaces.find((w) => w.slug === slug) ?? null,
    can: (permission) => orgSet.has(permission),
    canIn: (workspaceId, permission) =>
      workspacePermissionSet(role, grants, workspaceId).has(permission),
  };
}

export type OrgScope =
  | { status: 'loading' }
  | { status: 'error'; error: Error; retry: () => void }
  | ({ status: 'ready' } & OrgScopeData);

export const OrgScopeContext = createContext<OrgScope | null>(null);

/** Orgs, the active org and the caller's permissions, wherever the signed-in shell is mounted. */
export function useOrgScope(): OrgScope {
  const scope = useContext(OrgScopeContext);
  if (!scope)
    throw new Error('useOrgScope needs an OrgScopeContext provider (app/routing/org-scope)');
  return scope;
}

/** For pages under `RequireOrg`, where the scope is ready and the org in the URL resolved. */
export function useOrgContext(): OrgScopeData & { org: Organization; role: OrgRole } {
  const scope = useOrgScope();
  if (scope.status !== 'ready' || !scope.org || !scope.role)
    throw new Error('useOrgContext is only for routes under RequireOrg');
  return { ...scope, org: scope.org, role: scope.role };
}
